// ChatHeader — the per-conversation header shown above the message thread once a chat is active:
// the chat title (inline-renameable) + a ⋮ menu (Rename / Delete chat). Reuses the shell's
// rename/delete (same as the Chats page). Delete confirms first, then returns to a safe view.

import { useState, useRef } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import { useNavigate } from 'react-router-dom'
import { useRosShell } from '../RosShellContext'
import { SERIF_HEADING } from '../theme'

export function ChatHeader({ visible, actions }: { visible: boolean; actions?: React.ReactNode }) {
  const shell = useRosShell()
  const navigate = useNavigate()
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const [renaming, setRenaming] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const suppressBlurRef = useRef(false)

  // Show whenever the conversation has messages (NOT gated on liveSessionId — that's undefined during
  // a fresh chat's first turn, which previously hid the header mid-chat).
  if (!visible) return null
  const session = shell.sessions.find(s => s.id === shell.liveSessionId)
  const title = session?.intentSummary || 'New chat'
  // Rename/delete need a real session id; disable them until the session exists (first turn done).
  const canManage = !!shell.liveSessionId

  // fromMenu: the ⋮ menu closing on open steals focus from the field, firing a spurious blur we must
  // swallow. Double-click has no menu, so it must NOT suppress — otherwise the first real blur (click
  // away) is eaten and rename never commits.
  const startRename = (fromMenu = false) => {
    setEditValue(title === 'New chat' ? '' : title)
    suppressBlurRef.current = fromMenu
    setRenaming(true)
    setAnchor(null)
  }
  const commitRename = () => {
    if (!renaming) return
    const name = editValue.trim()
    if (name && shell.liveSessionId) shell.renameSession(shell.liveSessionId, name)
    setRenaming(false)
  }
  const onFieldBlur = () => {
    // The menu closing on rename-open steals focus from the autoFocus field, firing a spurious blur
    // that would instantly exit rename mode. Swallow that first blur; honor real ones (and Enter) after.
    if (suppressBlurRef.current) { suppressBlurRef.current = false; return }
    commitRename()
  }
  const doDelete = () => {
    setConfirmOpen(false)
    if (shell.liveSessionId) shell.deleteSession(shell.liveSessionId)  // resets to a safe view (/)
  }

  return (
    <Box sx={{ px: 3, flexShrink: 0 }}>
      {/* Centered to the same 820px content column as the message thread + a light hairline. */}
      <Box sx={{
        maxWidth: 820, mx: 'auto', minHeight: 48,
        display: 'flex', alignItems: 'center', gap: 1,
        borderBottom: '1px solid', borderColor: 'divider',
      }}>
      {renaming ? (
        <TextField
          autoFocus size="small" value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={onFieldBlur}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); commitRename() }
            else if (e.key === 'Escape') { suppressBlurRef.current = false; setRenaming(false) }
          }}
          sx={{
            flex: 1, maxWidth: 420,
            '& .MuiOutlinedInput-root': { borderRadius: 999, fontWeight: 600, fontSize: '0.95rem' },
            '& .MuiOutlinedInput-input': { py: 0.6 },
          }}
        />
      ) : (
        <Typography
          noWrap
          onDoubleClick={() => { if (canManage) startRename() }}
          sx={{
            flex: 1, fontWeight: 600, fontSize: '0.95rem', color: 'text.primary',
            cursor: canManage ? 'text' : 'default', borderRadius: 1, px: 0.5, mx: -0.5,
            '&:hover': canManage ? { bgcolor: 'action.hover' } : undefined,
          }}
          title={canManage ? 'Double-click to rename' : title}
        >
          {title}
        </Typography>
      )}

      {/* Project chip — shown when this chat belongs to a project; clicking opens the project. Hidden
          while renaming so the edit field has room. */}
      {!renaming && session?.projectId && session.projectName && (
        <Chip
          size="small"
          icon={<FolderOutlinedIcon />}
          label={session.projectName}
          onClick={() => navigate(`/projects/${session.projectId}`)}
          title={`In project: ${session.projectName}`}
          sx={{
            flexShrink: 0, maxWidth: 220, cursor: 'pointer', fontWeight: 500,
            bgcolor: 'rgba(0,143,168,0.10)', color: 'primary.main',
            '& .MuiChip-icon': { color: 'primary.main' },
            '&:hover': { bgcolor: 'rgba(0,143,168,0.18)' },
          }}
        />
      )}

      <IconButton size="small" onClick={e => setAnchor(e.currentTarget)} sx={{ color: 'text.secondary' }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      {/* The chat-level actions (export / panel / dev-mode) live in THIS row now, so there's no
          second header bar with its own divider. */}
      {actions && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>{actions}</Box>}
      {/* disableRestoreFocus: when the menu closes it would otherwise return focus to the ⋮ trigger,
          which steals focus from the freshly-mounted rename field → its onBlur fires → rename reverts
          instantly. Disabling restore lets the autoFocus rename input keep focus. */}
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} disableRestoreFocus anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={() => startRename(true)} disabled={!canManage}>
          <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); setConfirmOpen(true) }} disabled={!canManage} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: canManage ? 'error.main' : 'inherit' }} /></ListItemIcon>
          <ListItemText>Delete chat</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle sx={SERIF_HEADING}>Delete this chat?</DialogTitle>
        <DialogContent>
          <DialogContentText>“{title}” will be removed. This can't be undone from here.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={doDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  )
}
